import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { DynamicFormCriteriaFlowScope, FlowTypes } from "config";
import { RootState } from "store";

type AvailableFlowScopes = Exclude<
  DynamicFormCriteriaFlowScope,
  "coverage_adjustment" | "reinstatement"
>;

// Redirect user to next page on Flow form submit
export const useFlowRouteRedirect = (slug: FlowTypes) => {
  // HOOKS
  const navigate = useNavigate();
  const { state: _locationState } = useLocation();
  const locationState = _locationState as { from?: string } | null;
  const flowScope = useSelector((state: RootState) => state.formFlow.flowScope);

  // CALLBACKS
  const beneficiaryFlowLink = useCallback(() => {
    const isNewPolicyFlowScope = flowScope === "new_policy";

    const fallbackPaths: Record<AvailableFlowScopes, string> = {
      new_policy: "/flow/pre-underwriting",
      policy_holder: "/products/insurance",
    };
    if (!flowScope) {
      return "/";
    }
    if (isNewPolicyFlowScope) {
      return fallbackPaths.new_policy;
    }

    const fallbackPath = fallbackPaths[flowScope as AvailableFlowScopes];
    return locationState?.from || fallbackPath;
  }, [flowScope, locationState]);

  // METHODS
  const getNextFlowPath = () => {
    switch (slug) {
      case "beneficiary":
        return beneficiaryFlowLink();

      case "insurance-profile": {
        if (flowScope === "new_policy") {
          return "/flow/beneficiary";
        } else {
          return "/flow/pre-underwriting";
        }
      }

      case "referral-partner": {
        return "/coverage-preview";
      }

      case "onboarding-intro":
        return "/flow/insurance-profile";

      case "post-underwriting": {
        if (flowScope === "coverage_adjustment") {
          return "/products/insurance/confirm-update";
        } else if (flowScope === "reinstatement") {
          return "/review-quote";
        } else {
          return "/flow/quote-review";
        }
      }

      case "quote-review":
        return "/flow/purchase-policy";

      case "underwriting-interview": {
        if (flowScope === "coverage_adjustment") {
          return "/products/insurance/confirm-update";
        } else {
          return "/flow/post-underwriting";
        }
      }

      default:
        return "/";
    }
  };

  const redirectToNextPage = () => {
    const nextFlowPath = getNextFlowPath();
    const shouldUseReplace = ["referral-partner"].includes(slug);

    if (shouldUseReplace) {
      navigate(nextFlowPath, { replace: true });
    } else {
      navigate(nextFlowPath);
    }
  };

  return { redirectToNextPage };
};
